import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import ArcadeMark from '../components/ArcadeMark';

const ResetPassword = () => {
  const { t, API } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      toast.error(t('auth.missingResetToken'));
      return;
    }
    if (!password || !confirm) {
      toast.error(t('errors.requiredField'));
      return;
    }
    if (password !== confirm) {
      toast.error(t('errors.passwordMismatch'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      toast.success(t('auth.passwordResetSuccess'));
      navigate('/login');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t('auth.resetLinkInvalid'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden arcade-field grid-texture scanlines crt-vignette flex items-center justify-center p-4">
      <div className="relative z-10 w-full max-w-md animate-fade-in-up" data-testid="reset-password-card">
        {/* ONE cabinet: illuminated marquee header + underlight strip + control panel */}
        <div className="cabinet-shell">
          {/* MARQUEE header band */}
          <div className="cabinet-marquee">
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-lg bg-background/60 border border-primary/40 text-primary glow-amber mb-3">
              <ArcadeMark className="w-8 h-8" />
            </span>
            <p className="eyebrow text-[10px] text-primary/80 mb-1">Arcade Fleet · Stock Manager</p>
            <h1 className="font-display font-bold text-3xl tracking-[0.10em] leading-none">
              <span className="chrome-text">{t('auth.resetPasswordTitle')}</span>
            </h1>
            <div className="mt-2 flex items-center justify-center gap-2">
              <span className="h-px w-8 bg-gradient-to-r from-transparent to-secondary/70" />
              <span className="eyebrow text-[10px] text-secondary text-glow-magenta">Fleet Access</span>
              <span className="h-px w-8 bg-gradient-to-l from-transparent to-secondary/70" />
            </div>
          </div>

          {/* underlight strip that joins the two zones */}
          <div className="cabinet-strip" />

          {/* CONTROL PANEL — the form */}
          <div className="cabinet-panel">
            <p className="text-center text-sm text-muted-foreground mb-6">{t('auth.resetPasswordSubtitle')}</p>

            {!token ? (
              <div className="space-y-4 text-center">
                <p className="text-sm text-destructive font-mono">{t('auth.resetLinkInvalid')}</p>
                <Button variant="outline" className="w-full h-12" onClick={() => navigate('/login')}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t('auth.backToLogin')}
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="new-password" className="eyebrow text-[10px] text-muted-foreground">
                    {t('auth.newPassword')}
                  </Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t('auth.newPassword')}
                      className="h-12 font-mono pr-12 bg-input/70 focus-visible:ring-primary"
                      autoComplete="new-password"
                      data-testid="reset-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-2 top-1/2 -translate-y-1/2"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="eyebrow text-[10px] text-muted-foreground">
                    {t('auth.confirmNewPassword')}
                  </Label>
                  <Input
                    id="confirm-password"
                    type={showPassword ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder={t('auth.confirmNewPassword')}
                    className="h-12 font-mono bg-input/70 focus-visible:ring-primary"
                    autoComplete="new-password"
                    data-testid="reset-password-confirm-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 font-display font-bold uppercase tracking-[0.18em] bg-primary text-primary-foreground hover:bg-primary/90 glow-amber"
                  disabled={loading}
                  data-testid="reset-password-submit"
                >
                  {loading ? t('auth.resettingPassword') : t('auth.resetPasswordBtn')}
                </Button>
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="w-full text-xs text-muted-foreground hover:text-primary transition-colors flex items-center justify-center gap-1"
                >
                  <ArrowLeft className="w-3 h-3" />
                  {t('auth.backToLogin')}
                </button>
              </form>
            )}
          </div>
        </div>

        <p className="mt-5 text-center eyebrow text-[10px] text-muted-foreground/60">
          Secure reset · Press start
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
